import React, { useState, useEffect, useMemo, Suspense } from 'react';
import {
  Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Select, MenuItem, FormControl, InputLabel, TextField,
  IconButton, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Autocomplete, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { Canvas, useLoader } from '@react-three/fiber';
import { OrbitControls, Billboard } from '@react-three/drei';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';
import * as THREE from 'three';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const ORANGE = '#F97316';

// Modelo "Teeth" por Vegarian (Free3D — Licença Royalty Free, uso comercial permitido)
const MODELO_DENTES_SUP = '/models/arcada_dentes_superior.obj';
const MODELO_DENTES_INF = '/models/arcada_dentes_inferior.obj';

const COR_DENTE = '#fdf8ef';

const SITUACAO_CONFIG = {
  pendente:       { label: 'Pendente',       fill: '#f1f5f9', textColor: '#64748b' },
  em_andamento:   { label: 'Em andamento',   fill: '#fffbeb', textColor: '#b45309' },
  concluido:      { label: 'Concluído',      fill: '#f0fdf4', textColor: '#16a34a' },
  pre_existente:  { label: 'Pré-existente',  fill: '#eff6ff', textColor: '#2563eb' },
};

const FACES = [
  { key: 'cervical',   label: 'C' },
  { key: 'distal',     label: 'D' },
  { key: 'incisal',    label: 'I' },
  { key: 'mesial',     label: 'M' },
  { key: 'palatal',    label: 'P' },
  { key: 'vestibular', label: 'V' },
];

const DENTES_SUPERIOR = [18,17,16,15,14,13,12,11,21,22,23,24,25,26,27,28];
const DENTES_INFERIOR = [48,47,46,45,44,43,42,41,31,32,33,34,35,36,37,38];

function gerarPosicoesArco(lista, { xMax, yBase, zMin, zMax }) {
  const n = lista.length;
  return lista.map((numero, i) => {
    const t = n > 1 ? i / (n - 1) : 0.5;
    const x = (t * 2 - 1) * xMax;
    const shape = 1 - Math.pow(2 * t - 1, 2); // 0 nas pontas (molares), 1 no centro (incisivos)
    const z = zMin + (zMax - zMin) * shape;
    return { numero, pos: [x, yBase, z] };
  });
}

// Posições em espaço "bruto" (mesma escala dos vértices originais do .obj) —
// calibradas a partir do bounding box real medido de cada arquivo.
const POSICOES_SUPERIOR = gerarPosicoesArco(DENTES_SUPERIOR, { xMax: 0.365, yBase: 0.5, zMin: 0.05, zMax: 0.84 }).map(r => ({ ...r, arco: 'superior' }));
const POSICOES_INFERIOR = gerarPosicoesArco(DENTES_INFERIOR, { xMax: 0.338, yBase: 0.33, zMin: 0.11, zMax: 0.77 }).map(r => ({ ...r, arco: 'inferior' }));

// Centro/escala pra normalizar o conjunto (medidos do bounding box combinado das 4 peças)
const CENTRO = { x: 0, y: 0.4225, z: 0.449 };
const ALTURA_ALVO = 1.7;
const ALTURA_BRUTA = 0.929;
const ESCALA = ALTURA_ALVO / ALTURA_BRUTA;

const transformar = ([x, y, z]) => [
  (x - CENTRO.x) * ESCALA,
  (y - CENTRO.y) * ESCALA,
  (z - CENTRO.z) * ESCALA,
];

const REGIOES_DENTES = [...POSICOES_SUPERIOR, ...POSICOES_INFERIOR].map(r => ({
  ...r,
  pos: transformar(r.pos),
}));

// Área clicável em cima de cada dente — sem marcador visível parado; só
// aparece um contorno quando passa o mouse em cima, pra não poluir a arcada.
function Hotspot({ regiao, temProcedimento, onClick }) {
  const [hover, setHover] = useState(false);
  const cor = temProcedimento ? ORANGE : '#94a3b8';
  return (
    <Billboard position={regiao.pos}>
      <mesh
        onClick={(e) => { e.stopPropagation(); onClick(regiao.numero); }}
        onPointerOver={(e) => { e.stopPropagation(); setHover(true); document.body.style.cursor = 'pointer'; }}
        onPointerOut={() => { setHover(false); document.body.style.cursor = 'auto'; }}
      >
        <ringGeometry args={[0.065, 0.09, 32]} />
        <meshBasicMaterial color={cor} side={THREE.DoubleSide} transparent opacity={hover ? 1 : 0} depthTest={false} />
      </mesh>
    </Billboard>
  );
}

// Arcada dentária 3D real — modelo comprado na Free3D (Vegarian). Só os
// dentes (sem gengiva/boca), normalizada (centralizada e escalada) pra
// caber sempre no mesmo enquadramento.
function Arcada3D({ vista }) {
  const dentesSup = useLoader(OBJLoader, MODELO_DENTES_SUP);
  const dentesInf = useLoader(OBJLoader, MODELO_DENTES_INF);

  const partes = useMemo(() => {
    const aplicarMaterial = (obj, cor) => {
      const clone = obj.clone(true);
      clone.traverse((child) => {
        if (child.isMesh) {
          child.material = new THREE.MeshStandardMaterial({ color: cor, roughness: 0.45, metalness: 0 });
        }
      });
      return clone;
    };
    return {
      dentesSup: aplicarMaterial(dentesSup, COR_DENTE),
      dentesInf: aplicarMaterial(dentesInf, COR_DENTE),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dentesSup, dentesInf]);

  return (
    <group scale={ESCALA} position={[-CENTRO.x * ESCALA, -CENTRO.y * ESCALA, -CENTRO.z * ESCALA]}>
      {vista !== 'inferior' && <primitive object={partes.dentesSup} />}
      {vista !== 'superior' && <primitive object={partes.dentesInf} />}
    </group>
  );
}

const ODONTO_VAZIO = { dentes: {}, procedimentos: [] };
const hoje = () => new Date().toISOString().slice(0, 10);
const fmtData = (iso) => iso ? new Date(`${iso}T00:00:00`).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '—';
const formProcVazio = (denteInicial) => ({
  procedimento: '', situacao: 'pendente', data: hoje(),
  profissional: '', dentes: denteInicial ? [denteInicial] : [], faces: {}, obs: '',
});

const Odontograma = ({ pacienteId, pacienteNome, onAgendar }) => {
  const { user } = useAuth();
  const [dados, setDados] = useState(ODONTO_VAZIO);
  const [vista, setVista] = useState('toda'); // 'toda' | 'superior' | 'inferior'
  const [procedimentosCadastrados, setProcedimentosCadastrados] = useState([]);
  const [profissionaisClinica, setProfissionaisClinica] = useState([]);
  const [salvando, setSalvando] = useState(false);

  // Modal de procedimento — cria ou edita; um procedimento pode cobrir
  // vários dentes (e faces) de uma vez.
  const [modalProc, setModalProc] = useState(false);
  const [procEditandoId, setProcEditandoId] = useState(null);
  const [formProc, setFormProc] = useState(formProcVazio());

  useEffect(() => {
    api.get(`/pacientes/${pacienteId}/odontograma`)
      .then(r => setDados(r?.dados || r?.data?.dados || ODONTO_VAZIO))
      .catch(() => {});
    api.get('/procedimentos')
      .then(r => {
        const lista = Array.isArray(r) ? r : Array.isArray(r?.data) ? r.data : [];
        setProcedimentosCadastrados(lista);
      })
      .catch(() => {});
  }, [pacienteId]);

  useEffect(() => {
    if (!user?.clinicaId) return;
    api.get(`/clinicas/${user.clinicaId}/usuarios`)
      .then(r => {
        const lista = Array.isArray(r) ? r : Array.isArray(r?.data) ? r.data : [];
        setProfissionaisClinica(lista.filter(u => u.role === 'dentista' || u.role === 'dentista_parceiro'));
      })
      .catch(() => {});
  }, [user?.clinicaId]);

  const procedimentosDoDente = (numero) => (dados.procedimentos || []).filter(p => p.dentes?.includes(numero));

  // Clicar num dente (no 3D ou no dropdown) abre direto o procedimento: se
  // só tiver um vinculado, abre ele pra edição; senão, abre um novo com
  // aquele dente já selecionado.
  const handleClicarDente = (numero) => {
    const procs = procedimentosDoDente(numero);
    if (procs.length === 1) abrirEditarProcedimento(procs[0]);
    else abrirNovoProcedimento(numero);
  };

  // ── Procedimento: criar / editar / excluir ──
  const abrirNovoProcedimento = (denteInicial) => {
    setProcEditandoId(null);
    setFormProc(formProcVazio(denteInicial));
    setModalProc(true);
  };

  const abrirEditarProcedimento = (proc) => {
    setProcEditandoId(proc.id);
    setFormProc({
      procedimento: proc.procedimento, situacao: proc.situacao || 'pendente', data: proc.data || hoje(),
      profissional: proc.profissional || '', dentes: proc.dentes || [], faces: proc.faces || {}, obs: proc.obs || '',
    });
    setModalProc(true);
  };

  const toggleFace = (numero, faceKey) => {
    setFormProc(prev => {
      const atuais = prev.faces[numero] || [];
      const novas = atuais.includes(faceKey) ? atuais.filter(f => f !== faceKey) : [...atuais, faceKey];
      return { ...prev, faces: { ...prev.faces, [numero]: novas } };
    });
  };

  // Atalho pra marcar a arcada inteira (ou toda a boca) de uma vez, em vez
  // de clicar dente por dente — clicar de novo desmarca.
  const toggleArcada = (lista) => {
    setFormProc(prev => {
      const todosSelecionados = lista.every(n => prev.dentes.includes(n));
      const dentes = todosSelecionados
        ? prev.dentes.filter(n => !lista.includes(n))
        : [...new Set([...prev.dentes, ...lista])];
      const faces = { ...prev.faces };
      if (todosSelecionados) lista.forEach(n => delete faces[n]);
      else lista.forEach(n => { if (!faces[n]) faces[n] = []; });
      return { ...prev, dentes, faces };
    });
  };

  const handleSalvarProcedimento = async () => {
    if (!formProc.procedimento || formProc.dentes.length === 0) return;
    setSalvando(true);
    try {
      const res = procEditandoId
        ? await api.put(`/pacientes/${pacienteId}/odontograma/procedimentos/${procEditandoId}`, formProc)
        : await api.post(`/pacientes/${pacienteId}/odontograma/procedimentos`, formProc);
      setDados(res?.dados || res?.data?.dados || dados);
      setModalProc(false);
    } catch {}
    setSalvando(false);
  };

  const handleExcluirProcedimento = async () => {
    if (!procEditandoId || !window.confirm('Excluir este procedimento?')) return;
    setSalvando(true);
    try {
      const res = await api.delete(`/pacientes/${pacienteId}/odontograma/procedimentos/${procEditandoId}`);
      setDados(res?.dados || res?.data?.dados || dados);
      setModalProc(false);
    } catch {}
    setSalvando(false);
  };

  const handleAgendar = () => {
    setModalProc(false);
    if (onAgendar) onAgendar({ dente: formProc.dentes[0], procedimento: formProc.procedimento, pacienteNome });
  };

  const procedimentosOrdenados = [...(dados.procedimentos || [])].sort((a, b) => (b.data || '').localeCompare(a.data || ''));

  return (
    <Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.3fr 1fr' }, gap: 2.5, alignItems: 'start' }}>

        {/* Arcada 3D */}
        <Box sx={{
          position: 'relative', height: 380, borderRadius: 2, overflow: 'hidden',
          bgcolor: 'rgba(249,115,22,0.03)', border: '1px solid rgba(249,115,22,0.1)',
        }}>
          <ToggleButtonGroup
            exclusive size="small" value={vista}
            onChange={(e, v) => v && setVista(v)}
            sx={{
              position: 'absolute', top: 10, left: 10, zIndex: 1,
              bgcolor: 'background.paper', boxShadow: 1, borderRadius: 1.5,
            }}
          >
            <ToggleButton value="toda" sx={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'none', px: 1.25, '&.Mui-selected': { bgcolor: ORANGE, color: '#fff', '&:hover': { bgcolor: '#EA580C' } } }}>Toda</ToggleButton>
            <ToggleButton value="superior" sx={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'none', px: 1.25, '&.Mui-selected': { bgcolor: ORANGE, color: '#fff', '&:hover': { bgcolor: '#EA580C' } } }}>Superior</ToggleButton>
            <ToggleButton value="inferior" sx={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'none', px: 1.25, '&.Mui-selected': { bgcolor: ORANGE, color: '#fff', '&:hover': { bgcolor: '#EA580C' } } }}>Inferior</ToggleButton>
          </ToggleButtonGroup>

          <Canvas camera={{ position: [0, 1.25, 2.65], fov: 38 }}>
            <ambientLight intensity={0.85} />
            <directionalLight position={[2, 3, 3]} intensity={0.8} />
            <directionalLight position={[-2, 1, -2]} intensity={0.3} />
            <Suspense fallback={null}>
              <Arcada3D vista={vista} />
              {REGIOES_DENTES.filter(r => vista === 'toda' || r.arco === vista).map(r => (
                <Hotspot key={r.numero} regiao={r} temProcedimento={procedimentosDoDente(r.numero).length > 0} onClick={handleClicarDente} />
              ))}
            </Suspense>
            <OrbitControls
              enablePan={false}
              minDistance={1.2}
              maxDistance={5}
              target={[0, 0, 0.15]}
            />
          </Canvas>
        </Box>

        {/* Organização */}
        <Box sx={{
          borderRadius: 2, border: '1px solid rgba(249,115,22,0.12)', p: 2,
          height: 380, display: 'flex', flexDirection: 'column',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography fontWeight={700} fontSize="0.85rem">Organização</Typography>
            <Button size="small" startIcon={<AddCircleIcon fontSize="small" />} onClick={() => abrirNovoProcedimento()} sx={{ color: ORANGE, fontSize: '0.72rem' }}>
              Procedimento
            </Button>
          </Box>

          <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
            <InputLabel shrink>Selecionar dente</InputLabel>
            <Select
              value=""
              label="Selecionar dente"
              displayEmpty
              notched
              onChange={e => handleClicarDente(Number(e.target.value))}
            >
              <MenuItem value="" disabled>Escolha um dente...</MenuItem>
              {[...DENTES_SUPERIOR, ...DENTES_INFERIOR].map(n => {
                const qtd = procedimentosDoDente(n).length;
                return (
                  <MenuItem key={n} value={n}>
                    {n}{qtd > 0 ? ` — ${qtd} procedimento${qtd > 1 ? 's' : ''}` : ''}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          {procedimentosOrdenados.length === 0 ? (
            <Typography variant="caption" color="text.disabled">Nenhum procedimento registrado ainda — clique num dente ou adicione um procedimento acima.</Typography>
          ) : (
            <TableContainer sx={{ flex: 1, overflowY: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', textTransform: 'uppercase', color: 'text.disabled', bgcolor: 'background.paper' }}>Dentes</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', textTransform: 'uppercase', color: 'text.disabled', bgcolor: 'background.paper' }}>Procedimento</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', textTransform: 'uppercase', color: 'text.disabled', bgcolor: 'background.paper' }}>Situação</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', textTransform: 'uppercase', color: 'text.disabled', bgcolor: 'background.paper' }}>Data</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {procedimentosOrdenados.map(p => (
                    <TableRow key={p.id} hover onClick={() => abrirEditarProcedimento(p)} sx={{ cursor: 'pointer' }}>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem' }}>{(p.dentes || []).join(', ')}</TableCell>
                      <TableCell sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>{p.procedimento}</TableCell>
                      <TableCell>
                        <Chip label={SITUACAO_CONFIG[p.situacao || 'pendente']?.label} size="small"
                          sx={{ bgcolor: SITUACAO_CONFIG[p.situacao || 'pendente']?.fill, color: SITUACAO_CONFIG[p.situacao || 'pendente']?.textColor, fontWeight: 700, fontSize: '0.65rem', height: 20 }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>{fmtData(p.data)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Box>

      {/* Modal de procedimento — criar/editar, pode cobrir vários dentes/faces */}
      <Dialog open={modalProc} onClose={() => setModalProc(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography fontWeight={700}>{procEditandoId ? 'Editar procedimento' : 'Adicionar procedimento'}</Typography>
          <IconButton size="small" onClick={() => setModalProc(false)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1 }}>
            Situação do procedimento
          </Typography>
          <ToggleButtonGroup
            exclusive fullWidth size="small" value={formProc.situacao}
            onChange={(e, v) => v && setFormProc(prev => ({ ...prev, situacao: v }))}
            sx={{ mb: 2.5 }}
          >
            {Object.entries(SITUACAO_CONFIG).map(([key, cfg]) => (
              <ToggleButton key={key} value={key} sx={{
                fontSize: '0.7rem', fontWeight: 700, textTransform: 'none',
                '&.Mui-selected': { bgcolor: ORANGE, color: '#fff', '&:hover': { bgcolor: '#EA580C' } },
              }}>
                {cfg.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <Autocomplete
            freeSolo
            options={procedimentosCadastrados.map(p => p.nome)}
            value={formProc.procedimento}
            onChange={(e, v) => setFormProc(prev => ({ ...prev, procedimento: v || '' }))}
            onInputChange={(e, v) => setFormProc(prev => ({ ...prev, procedimento: v }))}
            renderInput={(params) => <TextField {...params} label="Procedimento" placeholder="Busque ou digite o nome..." size="small" />}
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: 'flex', gap: 0.75, mb: 1 }}>
            {[
              { label: 'Arcada superior', lista: DENTES_SUPERIOR },
              { label: 'Arcada inferior', lista: DENTES_INFERIOR },
              { label: 'Toda a arcada', lista: [...DENTES_SUPERIOR, ...DENTES_INFERIOR] },
            ].map(({ label, lista }) => {
              const ativo = lista.every(n => formProc.dentes.includes(n));
              return (
                <Chip
                  key={label} label={label} size="small" clickable
                  onClick={() => toggleArcada(lista)}
                  sx={{
                    fontWeight: 700, fontSize: '0.68rem',
                    bgcolor: ativo ? ORANGE : undefined, color: ativo ? '#fff' : undefined,
                    '&:hover': { bgcolor: ativo ? '#EA580C' : undefined },
                  }}
                />
              );
            })}
          </Box>

          <Autocomplete
            multiple
            options={[...DENTES_SUPERIOR, ...DENTES_INFERIOR]}
            getOptionLabel={n => String(n)}
            value={formProc.dentes}
            onChange={(e, novos) => {
              setFormProc(prev => {
                const faces = {};
                novos.forEach(n => { faces[n] = prev.faces[n] || []; });
                return { ...prev, dentes: novos, faces };
              });
            }}
            renderInput={(params) => <TextField {...params} label="Dentes" placeholder="Selecione os dentes..." size="small" />}
            sx={{ mb: formProc.dentes.length > 0 ? 1.5 : 2 }}
          />

          {formProc.dentes.map(numero => (
            <Box key={numero} sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Faces do dente {numero} <Box component="span" sx={{ color: 'text.disabled' }}>(opcional)</Box>
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                {FACES.map(f => {
                  const ativo = (formProc.faces[numero] || []).includes(f.key);
                  return (
                    <Chip
                      key={f.key} label={f.label} size="small" clickable
                      onClick={() => toggleFace(numero, f.key)}
                      title={f.label}
                      sx={{
                        fontWeight: 700, width: 30,
                        bgcolor: ativo ? ORANGE : undefined, color: ativo ? '#fff' : undefined,
                        '&:hover': { bgcolor: ativo ? '#EA580C' : undefined },
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
          ))}

          <Autocomplete
            freeSolo
            options={profissionaisClinica.map(p => p.nome)}
            value={formProc.profissional}
            onChange={(e, v) => setFormProc(prev => ({ ...prev, profissional: v || '' }))}
            onInputChange={(e, v) => setFormProc(prev => ({ ...prev, profissional: v }))}
            renderInput={(params) => <TextField {...params} label="Profissional" placeholder="Quem realizou (opcional)" size="small" />}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth size="small" type="date" label="Data" value={formProc.data || ''}
            onChange={e => setFormProc(prev => ({ ...prev, data: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth size="small" multiline rows={2}
            label="Observações" value={formProc.obs}
            onChange={e => setFormProc(prev => ({ ...prev, obs: e.target.value }))}
            placeholder="Ex: cárie profunda, sensibilidade..."
          />
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1.5, gap: 1, flexWrap: 'wrap' }}>
          {procEditandoId && (
            <IconButton size="small" onClick={handleExcluirProcedimento} disabled={salvando} sx={{ mr: 'auto', color: 'text.disabled', '&:hover': { color: '#ef4444' } }} title="Excluir">
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          )}
          {formProc.procedimento && (
            <IconButton size="small" onClick={handleAgendar} title="Agendar">
              <CalendarMonthIcon fontSize="small" sx={{ color: ORANGE }} />
            </IconButton>
          )}
          <Button size="small" onClick={() => setModalProc(false)}>Cancelar</Button>
          <Button size="small" variant="contained" onClick={handleSalvarProcedimento} disabled={salvando || !formProc.procedimento || formProc.dentes.length === 0}>
            {salvando ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Odontograma;
